import React, { useState } from 'react';
import { candidateServiceApi } from '../../services/candidateServiceApi';

interface ResumeUploadProps {
    onComplete?: (candidateId: string) => void;
}

const ResumeUpload: React.FC<ResumeUploadProps> = ({ onComplete }) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [step, setStep] = useState<'upload' | 'questions' | 'complete'>('upload');
    const [parsedData, setParsedData] = useState<any>(null);
    const [questions, setQuestions] = useState<Array<{ id: number; question: string }>>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answer, setAnswer] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [completionData, setCompletionData] = useState<any>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setError(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file');
            return;
        }

        try {
            setUploading(true);
            setError(null);

            // Get candidate_id from localStorage
            const authUser = localStorage.getItem('authUser');
            if (!authUser) {
                setError('Not authenticated. Please login first.');
                return;
            }

            const userData = JSON.parse(authUser);
            const candidateId = userData.id || userData.user_id || userData.candidate_id;

            if (!candidateId) {
                setError('Candidate ID not found in session');
                return;
            }

            // Upload resume
            const response = await candidateServiceApi.uploadResume(
                file,
                candidateId,
                userData.email
            );

            if (response.success && response.payload) {
                setSessionId(response.payload.session_id);
                setParsedData(response.payload.parsed_data_summary);
                setQuestions(response.payload.missing_questions || []);

                if (response.payload.missing_questions && response.payload.missing_questions.length > 0) {
                    setStep('questions');
                    setCurrentQuestionIndex(0);
                } else {
                    // No questions, complete immediately
                    await completeSession(response.payload.session_id);
                }
            } else {
                setError(response.message || 'Failed to upload resume');
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.response?.data?.message || 'Failed to upload resume. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleAnswerSubmit = async () => {
        if (!sessionId || !answer.trim()) {
            setError('Please provide an answer');
            return;
        }

        try {
            setError(null);
            const currentQuestion = questions[currentQuestionIndex];

            const response = await candidateServiceApi.answerQuestion(
                sessionId,
                currentQuestion.id,
                answer
            );

            if (response.success) {
                setAnswer('');

                // Move to next question or complete
                if (currentQuestionIndex < questions.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                } else {
                    // All questions answered, complete session
                    await completeSession(sessionId);
                }
            } else {
                setError(response.message || 'Failed to submit answer');
            }
        } catch (err: any) {
            console.error('Answer submission error:', err);
            setError(err.response?.data?.message || 'Failed to submit answer');
        }
    };

    const handleSkip = async () => {
        if (!sessionId) return;

        try {
            setError(null);
            const currentQuestion = questions[currentQuestionIndex];

            const response = await candidateServiceApi.skipQuestion(
                sessionId,
                currentQuestion.id
            );

            if (response.success) {
                setAnswer('');

                // Move to next question or complete
                if (currentQuestionIndex < questions.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                } else {
                    // All questions handled, complete session
                    await completeSession(sessionId);
                }
            } else {
                setError(response.message || 'Failed to skip question');
            }
        } catch (err: any) {
            console.error('Skip error:', err);
            setError(err.response?.data?.message || 'Failed to skip question');
        }
    };

    const completeSession = async (sessionIdToComplete: string) => {
        try {
            setError(null);
            const response = await candidateServiceApi.completeSession(sessionIdToComplete);

            if (response.success && response.payload) {
                setCompletionData(response.payload);
                setStep('complete');

                if (onComplete) {
                    onComplete(response.payload.candidate_id);
                }
            } else {
                setError(response.message || 'Failed to complete session');
            }
        } catch (err: any) {
            console.error('Complete session error:', err);
            setError(err.response?.data?.message || 'Failed to complete session');
        }
    };

    // Step 1: Upload
    if (step === 'upload') {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-semibold text-[#0B111E] mb-2">
                        Upload Your Resume
                    </h2>
                    <p className="text-sm text-[#757575] mb-6">
                        Upload your resume to automatically parse and extract your information
                    </p>

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    <div className="border-2 border-dashed border-[#CBCBCB] rounded-lg p-8 text-center mb-6">
                        <input
                            type="file"
                            id="resume-upload"
                            accept=".pdf,.docx,.doc,.txt"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <label
                            htmlFor="resume-upload"
                            className="cursor-pointer flex flex-col items-center"
                        >
                            <svg
                                className="w-12 h-12 text-[#015FA6] mb-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                            </svg>
                            <p className="text-base font-medium text-[#0B111E] mb-1">
                                {file ? file.name : 'Click to upload or drag and drop'}
                            </p>
                            <p className="text-sm text-[#757575]">
                                PDF, DOCX, DOC, or TXT (Max 16MB)
                            </p>
                        </label>
                    </div>

                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                            !file || uploading
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-[#015FA6] hover:bg-[#014a85]'
                        }`}
                    >
                        {uploading ? 'Uploading...' : 'Upload Resume'}
                    </button>

                    {parsedData && (
                        <div className="mt-6 p-4 bg-[#F9F9F9] rounded-lg">
                            <h3 className="text-sm font-semibold text-[#0B111E] mb-2">
                                Parsed Data
                            </h3>
                            <div className="space-y-1 text-sm">
                                <p>
                                    <span className="text-[#757575]">Name:</span>{' '}
                                    <span className="text-[#0B111E]">{parsedData.name}</span>
                                </p>
                                <p>
                                    <span className="text-[#757575]">Email:</span>{' '}
                                    <span className="text-[#0B111E]">{parsedData.email}</span>
                                </p>
                                <p>
                                    <span className="text-[#757575]">Skills:</span>{' '}
                                    <span className="text-[#0B111E]">{parsedData.skills_count}</span>
                                </p>
                                <p>
                                    <span className="text-[#757575]">Experience:</span>{' '}
                                    <span className="text-[#0B111E]">
                                        {parsedData.experience_years} years
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Step 2: Answer Questions
    if (step === 'questions') {
        const currentQuestion = questions[currentQuestionIndex];

        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-semibold text-[#0B111E]">
                                Complete Your Profile
                            </h2>
                            <span className="text-sm text-[#757575]">
                                Question {currentQuestionIndex + 1} of {questions.length}
                            </span>
                        </div>

                        <div className="w-full h-2 bg-[#F1F3F4] rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#015FA6] transition-all duration-300"
                                style={{
                                    width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
                                }}
                            ></div>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-base font-medium text-[#0B111E] mb-3">
                            {currentQuestion.question}
                        </label>
                        <textarea
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            className="w-full min-h-[120px] p-4 border border-[#CBCBCB] rounded-lg text-sm text-[#0B111E] placeholder-[#A5A5A5] focus:outline-none focus:border-[#015FA6] resize-none"
                            placeholder="Type your answer here..."
                        />
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleSkip}
                            className="flex-1 py-3 px-4 rounded-lg font-medium text-[#015FA6] border border-[#015FA6] hover:bg-[#F1F3F4] transition-colors"
                        >
                            Skip
                        </button>
                        <button
                            onClick={handleAnswerSubmit}
                            disabled={!answer.trim()}
                            className={`flex-1 py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                                !answer.trim()
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-[#015FA6] hover:bg-[#014a85]'
                            }`}
                        >
                            {currentQuestionIndex < questions.length - 1 ? 'Next' : 'Complete'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Step 3: Complete
    return (
        <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                </div>

                <h2 className="text-2xl font-semibold text-[#0B111E] mb-2">
                    Resume Uploaded Successfully!
                </h2>
                <p className="text-sm text-[#757575] mb-6">
                    Your profile has been updated with the information from your resume
                </p>

                {completionData && (
                    <div className="bg-[#F9F9F9] rounded-lg p-6 mb-6 text-left">
                        <h3 className="text-base font-semibold text-[#0B111E] mb-4">Profile Summary</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-[#757575]">Name</p>
                                <p className="text-[#0B111E] font-medium">
                                    {completionData.candidate_summary.name}
                                </p>
                            </div>
                            <div>
                                <p className="text-[#757575]">Email</p>
                                <p className="text-[#0B111E] font-medium">
                                    {completionData.candidate_summary.email}
                                </p>
                            </div>
                            <div>
                                <p className="text-[#757575]">Total Experience</p>
                                <p className="text-[#0B111E] font-medium">
                                    {completionData.candidate_summary.total_experience_years} years
                                </p>
                            </div>
                            <div>
                                <p className="text-[#757575]">Skills</p>
                                <p className="text-[#0B111E] font-medium">
                                    {completionData.candidate_summary.skills_count}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <button
                    onClick={() => (window.location.href = '/candidate/dashboard')}
                    className="w-full py-3 px-4 rounded-lg font-medium text-white bg-[#015FA6] hover:bg-[#014a85] transition-colors"
                >
                    Go to Dashboard
                </button>
            </div>
        </div>
    );
};

export default ResumeUpload;
